import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Star, Clock, Briefcase, Calendar, CheckCircle, Award, Mail, DollarSign, ChevronLeft, ChevronRight, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO } from 'date-fns';
import { ProfileImage } from '../components/ProfileImage';
import { useCurrency, CurrencySelector } from '../components/CurrencyProvider';

const BADGE_STYLES = {
  'Top Rated': 'bg-amber-100 text-amber-800 border border-amber-200',
  'Elite Trainer': 'bg-purple-100 text-purple-800 border border-purple-200',
  'Experienced Trainer': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Rising Star': 'bg-pink-100 text-pink-800 border border-pink-200',
  'Verified Profile': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  'Highly Rated': 'bg-orange-100 text-orange-800 border border-orange-200',
};
const getBadgeStyle = (badge) => BADGE_STYLES[badge] || 'bg-slate-100 text-slate-700 border border-slate-200';

export default function TrainerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { format: formatCurrency } = useCurrency();
  
  const [trainer, setTrainer] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(new Date());

  const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const fetchTrainer = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trainers/${id}`);
      setTrainer(response.data);
    } catch (error) {
      toast.error('Failed to load trainer profile');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews/trainer/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  }, [id]);

  const fetchCalendar = useCallback(async () => {
    try {
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      const response = await axios.get(`${API}/calendar/${id}?start=${weekStart.toISOString()}&end=${weekEnd.toISOString()}`);
      setCalendarEvents(response.data);
    } catch (error) {
      console.error('Failed to load calendar:', error);
    }
  }, [id, currentWeek]);

  const fetchAvailability = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/availability/${id}`);
      setAvailabilitySlots(response.data);
    } catch (error) {
      console.error('Failed to load availability:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchTrainer();
    fetchReviews();
    fetchCalendar();
    fetchAvailability();
  }, [fetchTrainer, fetchReviews, fetchCalendar, fetchAvailability]);

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 7 PM

  const getEventsForDay = (date) => {
    const dayOfWeek = (date.getDay() + 6) % 7; // Convert to Mon=0 format

    // Calendar events from the API
    const apiEvents = calendarEvents.filter(event => {
      const eventDate = parseISO(event.start_datetime);
      return isSameDay(eventDate, date);
    }).map(e => ({
      ...e,
      // For corporate view: hide personal details, show as "Busy"
      displayTitle: e.event_type === 'booked' ? 'Booked' : e.event_type === 'available' ? 'Available' : 'Busy',
      displayType: e.event_type === 'available' ? 'available' : 'busy'
    }));

    // Overlay recurring availability slots as green
    const slotEvents = availabilitySlots
      .filter(slot => slot.day_of_week === dayOfWeek)
      .map(slot => {
        const startHour = parseInt(slot.start_time.split(':')[0]);
        const endHour = parseInt(slot.end_time.split(':')[0]);
        const events = [];
        for (let h = startHour; h < endHour; h++) {
          // Skip if there's already a calendar event at this hour
          const hasConflict = apiEvents.some(ae => parseISO(ae.start_datetime).getHours() === h);
          if (!hasConflict) {
            events.push({
              id: `avail-${slot.id}-${h}`,
              title: 'Available',
              displayTitle: 'Available',
              displayType: 'available',
              start_datetime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, 0).toISOString(),
              event_type: 'available'
            });
          }
        }
        return events;
      })
      .flat();

    return [...slotEvents, ...apiEvents];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!trainer) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="trainer-profile-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <Link to="/" className="flex items-center gap-2" data-testid="logo">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <span className="font-outfit font-bold text-xl text-slate-900 hidden sm:block">Training Solutions</span>
              </Link>
            </div>

            {user?.user_type === 'corporate' && (
              <Button
                onClick={() => navigate(`/corporate/dashboard?book=${trainer.id}`)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
                data-testid="book-trainer-btn"
              >
                Book Trainer
              </Button>
            )}
            <CurrencySelector />
          </div>
        </div>
      </header>

      {/* Profile Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar */}
            <ProfileImage imagePath={trainer.avatar} name={trainer.name} size="lg" className="shadow-lg" />

            {/* Info */}
            <div className="flex-1">
              <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-2" data-testid="trainer-name">
                {trainer.name}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-slate-500 mb-4">
                {trainer.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {trainer.location}
                  </span>
                )}
                {trainer.experience_years > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {trainer.experience_years}+ years experience
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {trainer.total_trainings || 0} trainings completed
                </span>
              </div>

              {/* Contact Information */}
              {(trainer.email || trainer.phone) && (
                <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  {trainer.email && (
                    <a href={`mailto:${trainer.email}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors" data-testid="trainer-email-display">
                      <Mail className="w-4 h-4" />
                      <span className="text-sm font-medium">{trainer.email}</span>
                    </a>
                  )}
                  {trainer.phone && (
                    <a href={`tel:${trainer.phone}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors" data-testid="trainer-phone-display">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm font-medium">{trainer.phone}</span>
                    </a>
                  )}
                </div>
              )}

              {/* Badges */}
              {trainer.badges && trainer.badges.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="trainer-badges">
                  {trainer.badges.map((badge) => (
                    <span
                      key={badge}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${getBadgeStyle(badge)}`}
                      data-testid={`badge-${badge.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Award className="w-3.5 h-3.5" />
                      {badge}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${star <= Math.round(trainer.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                      />
                    ))}
                  </div>
                  <span className="font-semibold text-slate-900">{trainer.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-slate-400">({trainer.total_reviews || 0} reviews)</span>
                </div>
                
                {trainer.hourly_rate > 0 && (
                  <div className="flex items-center gap-1 text-slate-900">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-xl">{formatCurrency(trainer.hourly_rate)}</span>
                    <span className="text-slate-400">/hour</span>
                  </div>
                )}

                {user && user.id !== trainer.user_id && (
                  <Button
                    onClick={() => navigate(`/messages?with=${trainer.user_id}&name=${encodeURIComponent(trainer.name)}`)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="message-trainer-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="about" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
            <TabsTrigger value="about" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-about">
              About
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-calendar">
              Calendar
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-reviews">
              Reviews ({reviews.length})
            </TabsTrigger>
          </TabsList>

          {/* About Tab */}
          <TabsContent value="about" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Main Info */}
              <div className="md:col-span-2 space-y-6">
                {/* Bio */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">About</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {trainer.bio || 'No bio provided yet.'}
                  </p>
                </div>

                {/* Skills */}
                {trainer.skills?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {trainer.skills.map(skill => (
                        <span key={skill} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Training Types */}
                {trainer.training_types?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Training Types</h3>
                    <div className="flex flex-wrap gap-2">
                      {trainer.training_types.map(type => (
                        <span key={type} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Qualifications */}
                {trainer.qualifications?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Qualifications</h3>
                    <ul className="space-y-3">
                      {trainer.qualifications.map((qual) => (
                        <li key={qual} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{qual}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Certifications */}
                {trainer.certifications?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Certifications</h3>
                    <ul className="space-y-3">
                      {trainer.certifications.map((cert) => (
                        <li key={cert} className="flex items-start gap-2">
                          <Award className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{cert}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Industries */}
                {trainer.industry?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">Industries</h3>
                    <div className="flex flex-wrap gap-2">
                      {trainer.industry.map(ind => (
                        <span key={ind} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm">
                          {ind}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Weekly Availability */}
              {availabilitySlots.length > 0 && (
                <div className="md:col-span-3 bg-white rounded-xl border border-slate-200 p-6" data-testid="trainer-availability">
                  <h3 className="font-outfit font-semibold text-lg text-slate-900 mb-4">
                    <Clock className="w-5 h-5 inline mr-2 text-blue-600" />
                    Weekly Availability
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {DAY_NAMES.map((day, idx) => {
                      const daySlots = availabilitySlots.filter(s => s.day_of_week === idx);
                      if (daySlots.length === 0) return null;
                      return (
                        <div key={day} className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <p className="font-medium text-emerald-800 text-sm mb-1">{day}</p>
                          {daySlots.map(slot => (
                            <p key={slot.id} className="text-xs text-emerald-600">{slot.start_time} — {slot.end_time}</p>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            {/* No availability message */}
            {availabilitySlots.length === 0 && calendarEvents.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-center" data-testid="no-availability-msg">
                <p className="text-amber-800 font-medium">This trainer hasn't configured their availability yet.</p>
                <p className="text-amber-600 text-sm mt-1">You can still send a booking request — the trainer will confirm if available.</p>
              </div>
            )}

            {availabilitySlots.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-blue-700" data-testid="availability-hint">
                <strong>Availability:</strong> Green slots show when this trainer is available for bookings. Use the "Book Trainer" button to request a session.
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="trainer-calendar">
              {/* Calendar Header */}
              <div className="flex items-center justify-between p-4 border-b bg-slate-50">
                <h3 className="font-outfit font-semibold text-lg text-slate-900">
                  {format(weekStart, 'MMMM yyyy')}
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
                    data-testid="prev-week-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentWeek(new Date())}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                    data-testid="next-week-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  {/* Day Headers */}
                  <div className="grid grid-cols-8 border-b bg-slate-50">
                    <div className="p-3 text-sm font-medium text-slate-500 border-r"></div>
                    {weekDays.map((day) => (
                      <div
                        key={day.toISOString()}
                        className={`p-3 text-center border-r last:border-r-0 ${
                          isSameDay(day, new Date()) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="text-xs text-slate-500 uppercase">{format(day, 'EEE')}</div>
                        <div className={`text-lg font-semibold ${
                          isSameDay(day, new Date()) ? 'text-blue-600' : 'text-slate-900'
                        }`}>
                          {format(day, 'd')}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Time Slots */}
                  <div className="relative">
                    {hours.map(hour => (
                      <div key={hour} className="grid grid-cols-8 border-b last:border-b-0">
                        <div className="p-2 text-xs text-slate-400 text-right border-r bg-slate-50/50">
                          {hour}:00
                        </div>
                        {weekDays.map((day) => {
                          const dayEvents = getEventsForDay(day);
                          const eventsAtHour = dayEvents.filter(event => {
                            const eventHour = parseISO(event.start_datetime).getHours();
                            return eventHour === hour;
                          });
                          
                          return (
                            <div
                              key={day.toISOString()}
                              className={`relative h-12 border-r last:border-r-0 ${
                                isSameDay(day, new Date()) ? 'bg-blue-50/30' : ''
                              }`}
                            >
                              {eventsAtHour.map((event, eventIndex) => (
                                <div
                                  key={event.id}
                                  className={`absolute left-1 right-1 rounded px-2 py-1 text-xs font-medium truncate ${
                                    event.displayType === 'available'
                                      ? 'bg-emerald-100 text-emerald-700 border-l-4 border-emerald-500'
                                      : 'bg-red-100 text-red-700 border-l-4 border-red-500'
                                  }`}
                                  style={{ top: `${eventIndex * 24}px` }}
                                  title={event.displayTitle || event.title}
                                >
                                  {event.displayTitle || event.title}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-6 p-4 border-t bg-slate-50">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded"></div>
                  <span className="text-sm text-slate-600">Available — trainer is open for bookings</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span className="text-sm text-slate-600">Busy — trainer is not available</span>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="space-y-4" data-testid="reviews-list">
              {reviews.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No reviews yet</h3>
                  <p className="text-slate-500">This trainer hasn&apos;t received any reviews yet.</p>
                </div>
              ) : (
                reviews.map(review => (
                  <div key={review.id} className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-900">{review.corporate_name}</h4>
                        <p className="text-sm text-slate-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-slate-600">{review.comment}</p>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
